// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.campaign.spec.ts
import { ArrowLeft, CircleNotch } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { Link, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import AskPunttChat from "./chat";
import { CampaignDocsPanel } from "./components/CampaignDocsPanel";
import { CampaignThreadRail } from "./components/CampaignThreadRail";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import { useAnalytics } from "../../utils/analytics";
import { useAppSelector } from "../../utils/hooks";
import { useCreateAskPunttChat } from "../../utils/queries/askPuntt";
import {
  askPunttProjectKeys,
  useAskPunttProject,
} from "../../utils/queries/askPunttProjects";

// Route for the Campaigns launcher (back-link target). Kept as a literal so
// this component imports no route module — that keeps it embeddable and lets
// its test mock only @tanstack/react-router (no sibling-module mocks).
const CAMPAIGNS_LAUNCHER_PATH = "/ask-puntt/campaigns";

export default function AskPunttCampaign() {
  // Loose param read so the component carries no route-module import (mirrors
  // AskPunttChat). The campaign route always supplies `projectId`.
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const analytics = useAnalytics("ask_puntt_campaign");
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);

  const { data, isLoading, isError } = useAskPunttProject(projectId);
  const createChat = useCreateAskPunttChat();
  const queryClient = useQueryClient();

  // The shared-chat agent can mutate the project (description/context/files).
  // When the embedded chat reports a project-mutating turn, refetch the project
  // so the docs panel + header update without a manual refresh.
  const handleProjectMutated = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.project(projectId),
    });
  }, [queryClient, projectId]);

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState("");

  const project = data?.project;
  const chats = useMemo(() => data?.chats ?? [], [data?.chats]);
  const sharedChatId = project?.sharedChatId ?? null;

  // Snap the active thread to the project's shared chat whenever the project
  // changes. Keyed on `projectId` + `sharedChatId` (both stable within a
  // project) — NOT on `chats`, so a refetch after creating a personal chat
  // doesn't clobber the user's current selection. Navigating to a different
  // project (same mounted component, new param) resets the selection so the
  // previous project's thread never bleeds across.
  useEffect(() => {
    setActiveChatId(sharedChatId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sharedChatId]);

  useEffect(() => {
    if (projectId)
      analytics.capture("ask_puntt_campaign_viewed", { projectId });
  }, [projectId]);

  const handleCreatePersonalChat = async () => {
    const msg = newChatMessage.trim();
    if (!msg) return;
    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: "general",
        projectId,
        projectChatKind: "personal",
      });
      analytics.capture("ask_puntt_campaign_personal_chat_created", {
        projectId,
      });
      setNewChatMessage("");
      setNewChatOpen(false);
      setActiveChatId(result.chat._id);
    } catch (error) {
      console.error("Failed to create personal chat:", error);
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex h-full items-center justify-center bg-base-white"
        data-testid="campaign-loading"
      >
        <CircleNotch
          size={28}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-4 bg-base-white"
        data-testid="campaign-error"
      >
        <Text size="4" weight="medium" className="text-puntt-neutral-gray-11">
          We couldn&apos;t load this project
        </Text>
        <Link
          to={CAMPAIGNS_LAUNCHER_PATH}
          className="text-sm text-puntt-accent-9 hover:underline"
          data-testid="campaign-error-back"
        >
          ← Back to all projects
        </Link>
      </div>
    );
  }

  const members = project.members ?? [];
  const ownerLabel =
    project.role === "owner" ? "owned by you" : "shared with you";

  return (
    <div className="flex h-full min-h-0" data-testid="ask-puntt-campaign">
      <CampaignThreadRail
        chats={chats}
        sharedChatId={sharedChatId}
        currentUserId={currentUserId}
        activeChatId={activeChatId}
        onSelect={setActiveChatId}
        onNewPersonal={() => setNewChatOpen(true)}
        isCreating={createChat.isPending}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-puntt-neutral-gray-5 px-5">
          <Link
            to={CAMPAIGNS_LAUNCHER_PATH}
            aria-label="Back to all projects"
            data-testid="campaign-back-link"
            className="flex size-8 items-center justify-center rounded-md text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
          >
            <ArrowLeft size={18} />
          </Link>
          <div className="flex min-w-0 items-center gap-2.5">
            <h1
              className="truncate text-[15px] font-semibold text-puntt-neutral-gray-12"
              data-testid="campaign-title"
            >
              {project.name}
            </h1>
            <span className="hidden shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2.5 py-1 text-[13px] font-medium text-puntt-neutral-gray-11 sm:inline">
              {ownerLabel}
            </span>
          </div>
          <div className="flex-1" />
          {members.length > 0 && (
            <div
              className="flex items-center -space-x-2"
              data-testid="campaign-members"
              aria-label={`${members.length} project members`}
            >
              {members.slice(0, 5).map((m) => (
                <AvatarWithInitials
                  key={m._id}
                  size={7}
                  name={m.name ?? m.email ?? "Member"}
                  avatar={m.avatar ?? undefined}
                  className="ring-2 ring-puntt-neutral-gray-1"
                />
              ))}
              {members.length > 5 && (
                <span className="flex size-7 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-[10px] font-medium text-puntt-neutral-gray-11 ring-2 ring-puntt-neutral-gray-1">
                  +{members.length - 5}
                </span>
              )}
            </div>
          )}
        </header>

        {/* Chat + docs */}
        <div className="flex min-h-0 flex-1">
          <div
            className="flex min-w-0 flex-1 flex-col"
            data-testid="campaign-chat-area"
          >
            {activeChatId ? (
              <AskPunttChat
                key={activeChatId}
                chatId={activeChatId}
                onProjectMutated={handleProjectMutated}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Text size="2" className="text-puntt-neutral-gray-9">
                  Select a chat to get started.
                </Text>
              </div>
            )}
          </div>
          <CampaignDocsPanel
            files={project.files ?? []}
            description={project.description}
          />
        </div>
      </div>

      {/* New personal chat dialog */}
      <Dialog.Root open={newChatOpen} onOpenChange={setNewChatOpen}>
        <Dialog.Content
          maxWidth="460px"
          data-testid="campaign-new-chat-dialog"
          aria-label="Start a new personal chat"
        >
          <Dialog.Title>New personal chat</Dialog.Title>
          <Dialog.Description size="2" className="text-puntt-neutral-gray-11">
            Private to you. Puntt sees this project&apos;s context, but no one
            else on the project sees this chat.
          </Dialog.Description>
          <TextArea
            data-testid="campaign-new-chat-input"
            aria-label="Personal chat message"
            className="mt-3"
            placeholder="Ask Puntt to review something, gather context, or draft an update…"
            value={newChatMessage}
            onChange={(e) => setNewChatMessage(e.target.value)}
            style={{ height: 96 }}
          />
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                data-testid="campaign-new-chat-cancel"
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              data-testid="campaign-new-chat-submit"
              disabled={!newChatMessage.trim() || createChat.isPending}
              onClick={handleCreatePersonalChat}
            >
              {createChat.isPending ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : null}
              Start chat
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
